import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { useThemeContext } from '../../styles/themes/themes.js';
import { useTranslations } from '../../hooks/use_translations.js';
import { spacing2x, spacing1x, spacing4x } from '../../styles/spacing.js';
import { Button } from '../Button/Button.js';
import { IconArrowDown } from '../Icons/IconArrowDown.js';
import { IconArrowUp } from '../Icons/IconArrowUp.js';
import { LinkButton } from '../LinkButton/LinkButton.js';
import { FileCard } from './FileCard.js';
import { UploaderInfo } from './UploaderInfo.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

var StyledButton = styled(Button).withConfig({
  displayName: "UploaderBase__StyledButton",
  componentId: "sc-1hl3yz-0"
})(["&&{}"]);
var StyledContainer = styled.div.withConfig({
  displayName: "UploaderBase__StyledContainer",
  componentId: "sc-1hl3yz-1"
})(["&&{position:relative;width:100%;border:1px dashed ", ";border-radius:", ";text-align:center;padding:80px;}&& ", "{margin-inline-end:", ";margin-bottom:", ";}&& ", ":last-child{margin-inline-end:0;}"], function (_ref) {
  var theme = _ref.theme;
  return theme.neutralColors.rhino;
}, spacing2x, StyledButton, spacing2x, spacing2x, StyledButton);
var HiddenText = styled.p.withConfig({
  displayName: "UploaderBase__HiddenText",
  componentId: "sc-1hl3yz-2"
})(["&&{display:none;", ";}"], function (props) {
  return props.showText && "display: block;";
});
var DropOverlay = styled.div.withConfig({
  displayName: "UploaderBase__DropOverlay",
  componentId: "sc-1hl3yz-3"
})(["&&{position:absolute;top:0;bottom:0;left:0;right:0;background:#e5e8f8;border-radius:", ";z-index:10;display:flex;align-items:center;justify-content:center;}"], spacing2x);
var SelectActionsText = styled.div.withConfig({
  displayName: "UploaderBase__SelectActionsText",
  componentId: "sc-1hl3yz-4"
})(["&&{margin-block-start:", ";font-size:12px;line-height:16px;color:", ";}"], spacing1x, function (_ref2) {
  var theme = _ref2.theme;
  return theme.neutralColors.charcoal;
});
var ActionsContainer = styled.div.withConfig({
  displayName: "UploaderBase__ActionsContainer",
  componentId: "sc-1hl3yz-5"
})(["&&{margin-block-start:", ";}"], spacing2x);
var FileContainer = styled.div.withConfig({
  displayName: "UploaderBase__FileContainer",
  componentId: "sc-1hl3yz-6"
})(["&&{display:flex;flex-flow:wrap;width:100%;margin-top:", ";", ";}&& > div{margin:0 ", " ", " 0;width:calc(50% - ", ");}&& > div:nth-child(2n){margin-inline-end:0;}@media (max-width:448px){&& > div{width:200%;margin-inline-end:0}}"], spacing2x, function (props) {
  return !props.multiple && "justify-content: center;";
}, spacing4x, spacing4x, spacing2x);
var dnd = {
  useDrop: useDrop
};
var UploaderBase = /*#__PURE__*/React.forwardRef(function UploaderBase2(props, ref) {
  var _props$actions;
  var _useId = useId(1, "longText-"),
    _useId2 = _slicedToArray(_useId, 1),
    textId = _useId2[0];
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    showText = _React$useState2[0],
    setShowText = _React$useState2[1];
  var inputRef = React.useRef(null);
  var handleFileAdd = React.useCallback(function (item) {
    if (item) {
      var _props$files;
      var files = item.files;
      if (props.limit && (((_props$files = props.files) === null || _props$files === void 0 ? void 0 : _props$files.length) || 0) + files.length > props.limit) {
        return props.onError(new RangeError("Exceeded.File.Limit"));
      }
      if (props.accept) {
        var fileTypes = files.map(function (file) {
          return file.type;
        });
        if (!fileTypes.every(function (type) {
          var _props$accept;
          return (_props$accept = props.accept) === null || _props$accept === void 0 ? void 0 : _props$accept.includes(type);
        })) {
          return props.onError(new EvalError("Invalid.File.Format"));
        }
      }
      props.fileCallback(files);
    }
  }, [props]);
  var _dnd$useDrop = dnd.useDrop(function () {
      return {
        accept: [NativeTypes.FILE],
        drop: function drop(item) {
          handleFileAdd(item);
        },
        collect: function collect(monitor) {
          return {
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop()
          };
        }
      };
    }, [handleFileAdd]),
    _dnd$useDrop2 = _slicedToArray(_dnd$useDrop, 2),
    _dnd$useDrop2$ = _dnd$useDrop2[0],
    canDrop = _dnd$useDrop2$.canDrop,
    isOver = _dnd$useDrop2$.isOver,
    drop = _dnd$useDrop2[1];
  var isHoveringDrop = canDrop && isOver;
  var toggleText = function toggleText() {
    return setShowText(!showText);
  };
  return /*#__PURE__*/jsxs("div", {
    ref: ref,
    children: [/*#__PURE__*/jsxs(StyledContainer, {
      ref: drop,
      children: [isHoveringDrop && /*#__PURE__*/jsx(DropOverlay, {
        children: /*#__PURE__*/jsx(UploaderInfo, {
          infoText: props.infoText,
          limit: props.limit
        })
      }), /*#__PURE__*/jsxs("div", {
        children: [/*#__PURE__*/jsx(UploaderInfo, {
          infoText: props.infoText,
          limit: props.limit
        }), props.actions && /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsx(SelectActionsText, {
            "data-testid": "mntl-uploader-select-actions-text",
            children: t("Uploader.selectActionsInfo")
          }), /*#__PURE__*/jsx(ActionsContainer, {
            children: (_props$actions = props.actions) === null || _props$actions === void 0 ? void 0 : _props$actions.map(function (action, index) {
              if (action.fromComputer) {
                return /*#__PURE__*/jsx(StyledButton, {
                  label: action.label,
                  onClick: function onClick() {
                    var _inputRef$current;
                    return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.click();
                  }
                }, index);
              } else {
                return /*#__PURE__*/jsx(StyledButton, {
                  label: action.label,
                  onClick: action.onClick
                }, index);
              }
            })
          })]
        }), props.downloadSrc && /*#__PURE__*/jsx(LinkButton, {
          label: t("Uploader.downloadTemplate"),
          onClick: function onClick() {
            return templateDownload(props.downloadSrc, t("Uploader.downloadTemplate"));
          }
        }), props.shortHelpText && /*#__PURE__*/jsx("p", {
          children: props.shortHelpText
        }), props.longHelpText && /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsx(HiddenText, {
            id: textId,
            showText: showText,
            children: props.longHelpText
          }), /*#__PURE__*/jsx(LinkButton, {
            endAdornment: showText ? IconArrowUp : IconArrowDown,
            label: showText ? t("Uploader.seeLess") : t("Uploader.seeMore"),
            onClick: toggleText,
            variant: "secondary",
            iconSize: 16,
            "aria-controls": textId,
            "aria-expanded": showText
          })]
        })]
      }), props.files && /*#__PURE__*/jsx(FileContainer, {
        multiple: props.files.length > 1,
        children: props.files.map(function (item, index) {
          return /*#__PURE__*/jsx(FileCard, {
            name: item.file.name,
            size: item.file.size,
            index: index,
            onRemove: item.onRemove,
            success: item.success,
            error: item.error,
            sizeUploaded: item.sizeUploaded
          }, index);
        })
      })]
    }), /*#__PURE__*/jsx("input", {
      "aria-label": t("Uploader.upload"),
      ref: inputRef,
      style: {
        display: "none"
      },
      type: "file",
      multiple: true,
      onChange: function onChange(evt) {
        var files = [].slice.call(evt.target.files);
        if (!files.length) return;
        evt.target.value = "";
        return handleFileAdd({
          files: files
        });
      },
      accept: props.accept ? props.accept.toString() : void 0
    })]
  });
});
UploaderBase.displayName = "UploaderBase";
function templateDownload(src, filename) {
  var link = document.createElement("a");
  link.href = src;
  link.download = filename;
  link.style.display = "none";
  var evt = new window.MouseEvent("click", {
    view: window,
    bubbles: true,
    cancelable: true
  });
  document.body.appendChild(link);
  link.dispatchEvent(evt);
  document.body.removeChild(link);
}

export { StyledButton, UploaderBase, dnd };
